import {
  Component,
  Input,
  OnInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { stringifyLargeNumbers, textHasLargeNumber } from '@mode/shared/util-js';
import { FlamingoTypes } from '@mode/shared/contract-common';
import { testClipboardSupport, formatBytes, prepareDataForCopying } from '@mode/shared/util-js';
import { Observable, of, Subject } from 'rxjs';
import { FlamingoFacade } from '@mode/switch-views/contract';
import { HttpClient } from '@angular/common/http';
import { map, catchError, first } from 'rxjs/operators';
import { ToastService } from '@mode/shared/util-toast';

export const MAX_COPY_COMPAT = 600000; // For browsers that don't support the clipboard API
export const MAX_COPY_BYTES = 50000000; // Based on our browser performance testing

@Component({
  selector: 'mode-copy-dataset-button',
  templateUrl: './copy-dataset-button.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CopyDatasetButtonComponent implements OnInit, OnChanges {
  @Input() queryToken!: string;
  @Input() rowsCount!: string;
  @Input() datasetSizeBytes!: number;
  @Input() columns!: FlamingoTypes.ResultColumns;
  @Input() jsonToCopy!: string;

  private _destroy$ = new Subject<void>();

  //Retrieves the results for copying data. Prevents http call
  content$!: Observable<FlamingoTypes.RecordsResult['content']>;

  downloading!: boolean;
  copyTooltipHtml!: string;
  copyButtonHtml!: string;
  largeDatasetForCopy: string | null = null;
  isLargeClipboardEnabled!: boolean;
  isClipboardEnabled!: boolean;
  sizeLimitText!: string;
  copying = false;
  linkToDownload!: string;
  copyDisabled = false;
  sizeLimit!: number;

  constructor(
    private http: HttpClient,
    private _toastService: ToastService,
    private flamingoFacade: FlamingoFacade,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Note that clipboard support will only be available in SSL, so currently cannot test locally
    this.isLargeClipboardEnabled = testClipboardSupport(window);
    this.sizeLimit = this.isLargeClipboardEnabled ? MAX_COPY_BYTES : MAX_COPY_COMPAT;
    this.sizeLimitText = formatBytes(this.sizeLimit);
    this.setView();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const shouldUpdateView = changes.queryToken || changes.jsonToCopy;
    if (shouldUpdateView) {
      this.setView();
    }
  }

  setView(): void {
    this.copyButtonHtml = 'Copy';
    this.downloading = false;
    this.setCopyTooltip();
    this.content$ = this.flamingoFacade.getContent(this.queryToken);
    this.copyDisabled = this.isCopyDisabled();
    this.largeDatasetForCopy = null;
  }

  downloadOrCopyDataset($event: any) {
    if (this.largeDatasetForCopy != null) {
      // Pass through to copy-to-clipboard directive
      return;
    }

    if (!this.copyDisabled) {
      this.downloading = true;
      this.copyButtonHtml = 'Generating...';
      this.copyDisabled = this.isCopyDisabled();
      this.http
        .get(this.jsonToCopy, { responseType: 'json' })
        .pipe(
          first(),
          map((response) => {
            const result = JSON.stringify(response);
            const data = textHasLargeNumber(result) ? JSON.parse(stringifyLargeNumbers(result)) : JSON.parse(result);
            this.largeDatasetForCopy = prepareDataForCopying({ content: data, columns: this.columns });
            this.copyButtonHtml = 'Copy to clipboard';
            this.setCopyTooltip();
            this.downloading = false;
            this.copyDisabled = this.isCopyDisabled();
            this.changeDetector.markForCheck();
          }),
          catchError((error) => {
            this._toastService.error('Failed to retrieve data for copying');
            return of(error);
          })
        )
        .subscribe();
    }

    $event.preventDefault();
    $event.stopPropagation();
  }

  setCopyTooltip() {
    if (this.datasetSizeBytes > this.sizeLimit) {
      const partialCopy = `Dataset is ${formatBytes(this.datasetSizeBytes - this.sizeLimit)} over the ${
        this.sizeLimitText
      } limit for copying.`;
      this.copyTooltipHtml = this.isLargeClipboardEnabled
        ? partialCopy
        : `${partialCopy}<br /> Larger datasets can be copied with Chrome or Firefox.`;
    } else if (this.largeDatasetForCopy != null) {
      this.copyTooltipHtml = 'Copy all rows to clipboard';
    } else if (this.copying) {
      this.copyTooltipHtml = '';
    } else {
      this.copyTooltipHtml = `Generate results (Up to ${this.sizeLimitText})`;
    }
  }

  isCopyDisabled(): boolean {
    return this.copying || this.downloading || this.datasetSizeBytes > this.sizeLimit || !this.linkToDownload == null;
  }
}
